/** Ground for the M0 look-dev diorama: a trench, a pond, a road and a compound yard. */

import type { BiomeName } from '../render/palette';
import type { TerrainDef } from '../render/terrain/Terrain';
import { fbm, smoothstep, valueNoise } from '../render/terrain/noise';

/* The diorama is 64 x 48 m; the camera shows about 36 x 28 m of it. North is -z (up-screen). */

export const SIZE_X = 64;

export const SIZE_Z = 48;

export const trenchZ = (x: number): number => 6.2 + Math.sin(x * 0.28) * 1.6;

export const POND = { x: -12.5, z: -5.5, r: 6.2 };

/** Signed distance (metres) to the dirt road: south-east corner, up to the compound gate. */
export function roadDistance(x: number, z: number): number {
  // Two segments: (22, 16) -> (11, 5) -> (11, -4)
  const seg = (ax: number, az: number, bx: number, bz: number): number => {
    const vx = bx - ax;
    const vz = bz - az;
    const t = Math.min(1, Math.max(0, ((x - ax) * vx + (z - az) * vz) / (vx * vx + vz * vz)));
    return Math.hypot(x - (ax + vx * t), z - (az + vz * t));
  };
  return Math.min(seg(24, 18, 11, 5), seg(11, 5, 11, -4));
}

export function makeTerrainDef(biome: BiomeName, seed: number): TerrainDef {
  const relief = biome === 'desert' ? 0.75 : biome === 'arctic' ? 0.5 : 0.35;
  const pondDepth = (x: number, z: number): number => {
    const wobble = (valueNoise(x * 0.22, z * 0.22, seed + 5) - 0.5) * 3.2;
    const d = Math.hypot(x - POND.x, (z - POND.z) * 1.25) + wobble;
    return 1.35 * smoothstep(POND.r, POND.r * 0.35, d);
  };
  const compound = (x: number, z: number): number =>
    smoothstep(1.5, 4.5, x) * smoothstep(21.5, 19, x) * smoothstep(-13.5, -11, z) * smoothstep(3.5, 1.5, z);

  return {
    originX: -SIZE_X / 2,
    originZ: -SIZE_Z / 2,
    sizeX: SIZE_X,
    sizeZ: SIZE_Z,
    ramps: ['ground', 'groundAlt', 'dirt', 'rock'],
    baseSteps: biome === 'arctic' ? [3, 2, 1, 1] : biome === 'desert' ? [2, 1, 1, 1] : [2, 1, 1, 1],
    waterLevel: -0.28,
    waterRegion: { x: POND.x, z: POND.z, width: POND.r * 2 + 8, depth: 14.4 },
    frozen: biome === 'arctic',
    heightAt(x, z) {
      const flat = 1 - Math.max(compound(x, z), smoothstep(3.2, 1.2, roadDistance(x, z)));
      let h = (fbm(x * 0.06, z * 0.06, 3, seed) - 0.5) * 2 * relief * flat;
      if (biome === 'desert') h += Math.abs(Math.sin(x * 0.19 + z * 0.07 + fbm(x * 0.05, z * 0.05, 2, seed + 3) * 5)) * 0.35 * flat;
      // Trench: a 2 m slot with steep sides, 1.2 m deep, from the west edge to x = -2.
      const inRun = smoothstep(-0.5, -2.5, x);
      const across = Math.abs(z - trenchZ(x));
      h -= 1.25 * smoothstep(1.35, 0.85, across) * inRun;
      // Spoil heaped on the enemy (north) side of the trench.
      h += 0.35 * smoothstep(2.6, 1.6, across) * smoothstep(1.2, 1.7, across) * inRun * (z < trenchZ(x) ? 1 : 0.4);
      h -= pondDepth(x, z);
      return h;
    },
    materialAt(x, z) {
      const patchy = smoothstep(0.52, 0.7, fbm(x * 0.09, z * 0.09, 2, seed + 11));
      const road = smoothstep(2.4, 1.3, roadDistance(x, z));
      const yard = compound(x, z) * 0.9;
      const across = Math.abs(z - trenchZ(x));
      const trench = smoothstep(1.7, 1.1, across) * smoothstep(-0.5, -2.5, x);
      const bank = smoothstep(0.0, 0.35, pondDepth(x, z)) * smoothstep(0.9, 0.35, pondDepth(x, z));
      const stony = smoothstep(0.66, 0.8, fbm(x * 0.13, z * 0.13, 2, seed + 23));
      const dirt = Math.max(road, yard, trench * 1.4);
      const rockW = Math.max(bank * 1.2, stony * 0.9);
      return [Math.max(0.05, 1 - patchy - dirt - rockW), patchy * (1 - dirt), dirt, rockW];
    },
  };
}
